import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { memberApi, paymentApi } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

const MemberDetail = () => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch member details
        const memberResponse = await memberApi.getById(id);
        setMember(memberResponse.data);
        
        // Fetch payment status
        const statusResponse = await memberApi.getPaymentStatus(id);
        setPaymentStatus(statusResponse.data);
        
        // Fetch payment history
        const paymentsResponse = await paymentApi.getByMember(id);
        setPayments(paymentsResponse.data);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load member data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  if (!member) {
    return <div className="alert alert-warning"><i className="fas fa-exclamation-circle me-2"></i>Member not found</div>;
  }

  // Get the current fee from the fee history
  let currentFee = 0;
  if (member.monthly_fee_history && member.monthly_fee_history.length > 0) {
    currentFee = member.monthly_fee_history[member.monthly_fee_history.length - 1].amount;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="display-5 mb-0">{member.name}</h1>
            <div className="d-flex gap-2">
              <Link to={`/members/${id}/edit`} className="btn btn-outline-secondary">
                <i className="fas fa-edit me-2"></i>Edit Member
              </Link>
              <Link to="/payments/new" className="btn btn-success">
                <i className="fas fa-plus-circle me-2"></i>Add Payment
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-user me-2"></i>
                Member Details
              </h5>
            </div>
            <div className="card-body p-0">
              <table className="table table-hover mb-0">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{member.name}</td>
                  </tr>
                  <tr>
                    <th>Username</th>
                    <td>{member.username || '-'}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{member.email || '-'}</td>
                  </tr>
                  <tr>
                    <th>Referer</th>
                    <td>{member.referer || '-'}</td>
                  </tr>
                  <tr>
                    <th>Service</th>
                    <td>{member.service || ''}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <span className={`badge ${member.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                        {member.status === 'active' ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Payment Type</th>
                    <td>
                      {member.payment_type === 'PP' ? 'PayPal' : 
                       member.payment_type === 'Ü' ? 'Bank Transfer' :
                       member.payment_type === '+' ? 'Paid by other' :
                       member.payment_type === '-' ? 'Disabled' : '-'}
                    </td>
                  </tr>
                  {member.paid_by_member_id && (
                    <tr>
                      <th>Paid By</th>
                      <td>
                        <Link to={`/members/${member.paid_by_member_id}`}>
                          View Paying Member
                        </Link>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>Free Membership</th>
                    <td>{member.is_free ? 'Yes' : 'No'}</td>
                  </tr>
                  <tr>
                    <th>Current Fee</th>
                    <td>{currentFee.toFixed(2)} €</td>
                  </tr>
                  <tr>
                    <th>Start Date</th>
                    <td>{formatDate(member.start_date)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                Payment Status
              </h5>
            </div>
            <div className="card-body p-0">
              {paymentStatus ? (
                <table className="table table-hover mb-0">
                  <tbody>
                    <tr>
                      <th>Paid Until</th>
                      <td>
                        {paymentStatus.paid_until 
                          ? formatDate(paymentStatus.paid_until) 
                          : 'Not paid'}
                      </td>
                    </tr>
                    <tr>
                      <th>Months Paid in Advance</th>
                      <td>{paymentStatus.months_paid_in_advance}</td>
                    </tr>
                    <tr>
                      <th>Missing Months</th>
                      <td>
                        {paymentStatus.missing_months && paymentStatus.missing_months.length > 0 
                          ? paymentStatus.missing_months.join(', ') 
                          : 'None'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div>No payment status available</div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="fas fa-history me-2"></i>
                Payment History
              </h5>
              <span className="badge bg-primary rounded-pill px-3 py-2">
                {payments.length} payments
              </span>
            </div>
            <div className="card-body p-0">
              {payments.length === 0 ? (
                <div className="alert alert-info m-3">
                  <i className="fas fa-info-circle me-2"></i>
                  No payment history available
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead >
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Valid From</th>
                        <th>Valid Until</th>
                        <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(payment => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.paid_on)}</td>
                          <td>{payment.amount.toFixed(2)} €</td>
                          <td>
                            {payment.valid_from 
                              ? formatDate(payment.valid_from) 
                              : 'N/A'}
                          </td>
                          <td>
                            {payment.valid_until 
                              ? formatDate(payment.valid_until) 
                              : 'N/A'}
                          </td>
                          <td className="text-end">
                            <Link to={`/payments/${payment.id}/edit`} className="btn btn-sm btn-outline-secondary">
                              <i className="fas fa-edit"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDetail;
